import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  isEnriching: boolean;
  customField: string;
  customFieldPrompt: string;
  enrichError: string | null;

  setCustomField: (field: string) => void;

  setCustomFieldPrompt: (prompt: string) => void;

  handleCloseEnrichDialog: () => void;

  handleSubmitEnrichDialog: () => void;
}

const LeadListEnrichmentDialog: React.FC<Props> = ({
  open,
  isEnriching,
  customField,
  customFieldPrompt,
  enrichError,
  setCustomField,
  setCustomFieldPrompt,
  handleCloseEnrichDialog,
  handleSubmitEnrichDialog,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} transitionDuration={0} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('enrichLeadList')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <PaiTextField
            label={t('customFieldNameOptional')}
            value={customField}
            onChange={(e) => setCustomField(e.target.value)}
            placeholder={t('customFieldNamePlaceholder')}
            variant={'white'}
            disabled={isEnriching}
          />
          <PaiTextField
            label={t('customFieldPromptOptional')}
            value={customFieldPrompt}
            onChange={(e) => setCustomFieldPrompt(e.target.value)}
            placeholder={t('customFieldPromptPlaceholder')}
            variant={'white'}
            disabled={isEnriching}
          />
          <Divider />
          <Alert className={classes.alert} severity="info">
            {t('customFieldInfo')}
          </Alert>
          {enrichError && (
            <Alert className={classes.alert} severity="error">
              {enrichError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleCloseEnrichDialog} />
        <PaiButton text={t('enrichNow')} action={handleSubmitEnrichDialog} size={'small'} border={'rounded'} loading={isEnriching} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
    '&.highlight': {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
  },
  alert: {
    margin: '0 !important',
    borderRadius: '8px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default LeadListEnrichmentDialog;
